import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, UploadCloud, Sparkles, Loader2, Trash2, UserPlus, AlertTriangle, CheckCircle2 } from "lucide-react";
import { COMPANY_DOC_CATEGORIES, WORKER_DOC_CATEGORIES, SITE_DOC_CATEGORIES, EQUIPMENT_DOC_CATEGORIES } from "../lib/utils-app";

const CATS = { company: COMPANY_DOC_CATEGORIES, worker: WORKER_DOC_CATEGORIES, site: SITE_DOC_CATEGORIES, equipment: EQUIPMENT_DOC_CATEGORIES };
const SECTIONS = [["company", "Azienda"], ["worker", "Lavoratore"], ["site", "Cantiere"], ["equipment", "Attrezzatura"]];
const EMPTY_WORKER = { nome: "", cognome: "", codice_fiscale: "", data_nascita: "", mansione: "" };
const inp = "w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white";

export default function BulkUploadDialog({ companyId, companyName, onClose, onDone }) {
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState(null);
  const [ctx, setCtx] = useState({ workers: [], sites: [], equipment: [] });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    if (!files.length) { toast.error("Seleziona almeno un file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("company_id", companyId);
      files.forEach(f => fd.append("files", f));
      const { data } = await api.post("/documents/bulk-analyze", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      setItems(data.items); setCtx(data.context);
      toast.success(`${data.items.length} documenti analizzati. Verifica e conferma.`);
    } catch { toast.error("Errore durante l'analisi AI"); }
    finally { setBusy(false); }
  };

  const update = (i, patch) => setItems(items.map((it, k) => k === i ? { ...it, ...patch } : it));
  const removeItem = (i) => setItems(items.filter((_, k) => k !== i));

  const confirm = async () => {
    for (const it of items) {
      if (!it.categoria) { toast.error(`Categoria mancante per ${it.filename}`); return; }
      if (it.entity_type !== "company" && !it.entity_id && !(it.entity_type === "worker" && it.new_worker && (it.new_worker.nome || it.new_worker.cognome))) {
        toast.error(`Destinazione mancante per ${it.filename}`); return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/documents/bulk-confirm", { company_id: companyId, items });
      setResult(data);
      toast.success(`${data.created} documenti archiviati`);
      onDone && onDone();
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bulk-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Sparkles size={18} className="text-blue-700" /> Caricamento massivo con AI</h3>
            <p className="text-xs text-slate-500">{companyName} · i documenti vengono letti, classificati e smistati automaticamente</p>
          </div>
          <button onClick={onClose} data-testid="bulk-close"><X size={20} /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {result ? <ResultView result={result} /> : !items ? (
            <FilePicker files={files} setFiles={setFiles} busy={busy} analyze={analyze} />
          ) : (
            <ProposalTable items={items} ctx={ctx} update={update} removeItem={removeItem} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          {result ? (
            <button onClick={onClose} className="px-4 py-2 bg-slate-900 text-white rounded-lg" data-testid="bulk-finish">Chiudi</button>
          ) : items ? (
            <>
              <button onClick={() => setItems(null)} className="px-4 py-2 text-slate-600">Indietro</button>
              <button onClick={confirm} disabled={busy || !items.length} data-testid="bulk-confirm"
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg disabled:opacity-60 inline-flex items-center gap-2">
                {busy && <Loader2 size={16} className="animate-spin" />} Conferma e archivia {items.length} documenti
              </button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FilePicker({ files, setFiles, busy, analyze }) {
  const onDrop = (e) => { e.preventDefault(); setFiles([...files, ...Array.from(e.dataTransfer.files)]); };
  return (
    <div className="space-y-4">
      <label className="dropzone block cursor-pointer" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
        <input type="file" multiple className="hidden" data-testid="bulk-file-input"
          onChange={(e) => setFiles([...files, ...Array.from(e.target.files)])} />
        <div className="flex flex-col items-center gap-2 text-slate-500 py-6">
          <UploadCloud size={32} />
          <span className="text-sm">Trascina qui più file (PDF o immagini) oppure clicca per selezionarli</span>
        </div>
      </label>
      {files.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}-${f.lastModified}`} className="flex items-center justify-between px-3 py-2 text-sm">
              <span className="truncate font-mono text-xs">{f.name} <span className="text-slate-400">({Math.round(f.size / 1024)} KB)</span></span>
              <button onClick={() => setFiles(files.filter((_, k) => k !== i))} className="text-red-600 p-1"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <button onClick={analyze} disabled={busy || !files.length} data-testid="bulk-analyze"
        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-slate-900 text-white font-medium py-2.5 rounded-lg disabled:opacity-60 transition">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {busy ? `Analisi AI di ${files.length} documenti in corso…` : `Analizza ${files.length} documenti con AI`}
      </button>
    </div>
  );
}

function ProposalTable({ items, ctx, update, removeItem }) {
  const entityOptions = (type) => type === "worker" ? ctx.workers.map(w => [w.id, `${w.cognome} ${w.nome}`])
    : type === "site" ? ctx.sites.map(s => [s.id, s.denominazione])
    : type === "equipment" ? ctx.equipment.map(e => [e.id, `${e.tipologia} ${e.matricola || ""}`]) : [];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[980px]">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
          <tr>
            <th className="text-left px-2 py-2">File</th>
            <th className="text-left px-2 py-2 w-32">Sezione</th>
            <th className="text-left px-2 py-2 w-52">Destinazione</th>
            <th className="text-left px-2 py-2 w-52">Categoria</th>
            <th className="text-left px-2 py-2">Nome (standard)</th>
            <th className="text-left px-2 py-2 w-36">Emissione</th>
            <th className="text-left px-2 py-2 w-36">Scadenza</th>
            <th className="text-left px-2 py-2 w-20">AI</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const cats = CATS[it.entity_type] || [];
            const catKnown = cats.includes(it.categoria);
            const isNewWorker = it.entity_type === "worker" && !it.entity_id;
            return (
              <React.Fragment key={it.temp_id}>
                <tr className="border-t border-slate-100 align-top" data-testid={`bulk-row-${i}`}>
                  <td className="px-2 py-2">
                    <div className="font-mono text-xs break-all">{it.filename}</div>
                    {it.error && <div className="text-xs text-amber-700 flex items-center gap-1 mt-1"><AlertTriangle size={12} /> {it.error}</div>}
                  </td>
                  <td className="px-2 py-2">
                    <select value={it.entity_type} className={inp} data-testid={`bulk-section-${i}`}
                      onChange={(e) => update(i, { entity_type: e.target.value, entity_id: "", categoria: "", new_worker: e.target.value === "worker" ? { ...EMPTY_WORKER } : null })}>
                      {SECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    {it.entity_type === "company" ? <span className="text-xs text-slate-500">Documenti aziendali</span> : (
                      <select value={it.entity_id || (isNewWorker ? "__new__" : "")} className={inp} data-testid={`bulk-entity-${i}`}
                        onChange={(e) => update(i, e.target.value === "__new__" ? { entity_id: "", new_worker: it.new_worker || { ...EMPTY_WORKER } } : { entity_id: e.target.value })}>
                        <option value="">— seleziona —</option>
                        {it.entity_type === "worker" && <option value="__new__">+ Nuovo lavoratore</option>}
                        {entityOptions(it.entity_type).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <select value={catKnown ? it.categoria : "__custom__"} className={inp}
                      onChange={(e) => update(i, { categoria: e.target.value === "__custom__" ? (catKnown ? "" : it.categoria) : e.target.value })}>
                      <option value="">— categoria —</option>
                      {cats.map(c => <option key={c} value={c}>{c}</option>)}
                      <option value="__custom__">Personalizzata…</option>
                    </select>
                    {!catKnown && <input value={it.categoria} placeholder="Categoria personalizzata" className={`${inp} mt-1`} onChange={(e) => update(i, { categoria: e.target.value })} />}
                  </td>
                  <td className="px-2 py-2">
                    <input value={it.nome} className={inp} data-testid={`bulk-nome-${i}`} onChange={(e) => update(i, { nome: e.target.value })} />
                    {(it.corso || it.durata_ore || it.ente_formatore || it.normativa) && (
                      <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                        {it.corso && <div>Corso: {it.corso}{it.tipologia_formazione && ` (${it.tipologia_formazione})`}{it.durata_ore ? ` · ${it.durata_ore} ore` : ""}</div>}
                        {it.ente_formatore && <div>Ente: {it.ente_formatore}</div>}
                        {it.normativa && <div>Norma: {it.normativa}</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2"><input type="date" value={it.data_emissione} className={inp} onChange={(e) => update(i, { data_emissione: e.target.value })} /></td>
                  <td className="px-2 py-2"><input type="date" value={it.data_scadenza} className={inp} onChange={(e) => update(i, { data_scadenza: e.target.value })} /></td>
                  <td className="px-2 py-2">
                    {it.affidabilita > 0 && <span data-testid={`bulk-aff-${i}`} className={`px-2 py-0.5 rounded-full text-xs font-semibold ${it.affidabilita >= 80 ? "bg-emerald-100 text-emerald-800" : it.affidabilita >= 50 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{it.affidabilita}%</span>}
                  </td>
                  <td className="px-2 py-2"><button onClick={() => removeItem(i)} className="text-red-600 p-1" title="Escludi"><Trash2 size={15} /></button></td>
                </tr>
                {isNewWorker && it.new_worker && (
                  <tr className="bg-blue-50/60">
                    <td colSpan={9} className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className="inline-flex items-center gap-1 font-semibold text-blue-800"><UserPlus size={13} /> Nuovo lavoratore:</span>
                        {[["cognome", "Cognome"], ["nome", "Nome"], ["codice_fiscale", "Codice fiscale"], ["mansione", "Mansione"]].map(([k, l]) => (
                          <input key={k} placeholder={l} value={it.new_worker[k] || ""} data-testid={`bulk-newworker-${k}-${i}`}
                            className="px-2 py-1 border border-slate-300 rounded-md text-xs w-40 bg-white"
                            onChange={(e) => update(i, { new_worker: { ...it.new_worker, [k]: e.target.value } })} />
                        ))}
                        <input type="date" value={it.new_worker.data_nascita || ""} className="px-2 py-1 border border-slate-300 rounded-md text-xs bg-white"
                          onChange={(e) => update(i, { new_worker: { ...it.new_worker, data_nascita: e.target.value } })} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ResultView({ result }) {
  return (
    <div className="space-y-4" data-testid="bulk-result">
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <CheckCircle2 className="text-emerald-600" size={28} />
        <div>
          <div className="font-semibold text-emerald-900">{result.created} documenti archiviati nelle rispettive sezioni</div>
          {result.workers_created?.length > 0 && <div className="text-sm text-emerald-800">{result.workers_created.length} nuovi lavoratori creati: {result.workers_created.map(w => `${w.cognome} ${w.nome}`).join(", ")}</div>}
        </div>
      </div>
      {result.errors?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
          <div className="font-semibold mb-1">Non archiviati:</div>
          {result.errors.map((e) => <div key={e.filename} className="font-mono text-xs">{e.filename} — {e.error}</div>)}
        </div>
      )}
      <p className="text-xs text-slate-500">Il testo dei documenti viene indicizzato in background per la ricerca full-text.</p>
    </div>
  );
}
